import type { RiskResult } from "@gram/client/models/components/riskresult.js";
import { jsonEscaped } from "./chatHelpers";

export type ToolRiskField = "tool.args" | "tool_result";

export interface ToolSectionRiskMatch {
  value: string;
  result: RiskResult;
}

/**
 * Returns only findings that can be rendered inside a specific tool section.
 *
 * A tool-request result belongs to the whole chat message, so it may describe
 * the function name rather than its arguments. Newer results attribute every
 * span to a field; legacy detectors do not, so those are retained only when the
 * matched value is actually present in the section content.
 */
export function toolSectionRiskMatches(
  results: RiskResult[] | undefined,
  content: string | undefined,
  field: ToolRiskField,
): ToolSectionRiskMatch[] {
  if (!results?.length || !content) return [];

  // Deduplicate identical spans within one finding, but keep one entry per
  // finding even when two findings flag the same value — dropping a finding
  // would hide it from the badge and could skip masking (gitleaks/presidio).
  const byFindingValue = new Map<string, ToolSectionRiskMatch>();
  for (const result of results) {
    const spans =
      result.spans && result.spans.length > 0
        ? result.spans
        : result.match
          ? [{ match: result.match }]
          : [];

    for (const span of spans) {
      if (!span.match || (span.field && span.field !== field)) continue;
      // JSON sections hold the value escaped, so match that form too.
      const value = [span.match, jsonEscaped(span.match)].find((m) =>
        content.includes(m),
      );
      if (!value) continue;
      const key = `${result.id} ${value}`;
      if (byFindingValue.has(key)) continue;
      byFindingValue.set(key, { value, result });
    }
  }

  return [...byFindingValue.values()].toSorted(
    (a, b) => b.value.length - a.value.length,
  );
}
